import { useEffect, useRef, useState } from "react";
import { getFeatureFlagState } from "../services/featureFlags";
import { hasValidToken } from "../utils/cookies";

const CACHE_TTL_MS = 30_000;
const stateCache = new Map();

/**
 * Hook que consulta o estado da feature flag para o usuário logado e cacheia
 * o resultado em memória por ~30s. Enquanto carrega, retorna { loading: true }
 * com defaults seguros (não bloqueia para evitar flicker da UI).
 */
export const useFeatureFlag = (key) => {
  const [state, setState] = useState(() => readFromCache(key));
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!key) return;

    if (!hasValidToken()) {
      if (isMounted.current) {
        setState({
          key,
          isEnabled: false,
          blocked: false,
          loading: false,
        });
      }
      return;
    }

    const cached = readFromCache(key);
    if (cached && !cached.loading) {
      setState(cached);
      return;
    }

    let cancelled = false;
    (async () => {
      const resolved = await getFeatureFlagState(key);
      const next = { ...resolved, loading: false };
      writeToCache(key, next);
      if (!cancelled && isMounted.current) {
        setState(next);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
};

function readFromCache(key) {
  if (!key) return { key, isEnabled: false, blocked: false, loading: false };
  const entry = stateCache.get(key);
  if (entry && entry.expiresAt > Date.now()) {
    return entry.value;
  }
  return { key, isEnabled: false, blocked: false, loading: true };
}

function writeToCache(key, value) {
  if (!key) return;
  stateCache.set(key, {
    value,
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
}

export const __resetFeatureFlagCacheForTests = () => {
  stateCache.clear();
};
